import type { TrelloRestResolver } from '../types';
import { prepareDataForApolloCache } from '../prepareDataForApolloCache';
import { parseNetworkError } from '@trello/graphql-error-handling';
import { sendNetworkErrorEvent } from '@trello/error-reporting';
import { getNetworkClient } from '../getNetworkClient';
import { token } from '@trello/session-cookie';
import { trelloFetch, fetch } from '@trello/fetch';
import {
  MutationMigrateEnterpriseToAtlassianArgs,
  MutationUpdateEnterpriseMultipleEmailMembersPrimaryEmailArgs,
  QueryEnterpriseMultipleEmailMembersArgs,
} from '../generated';
import { isQueryInfo } from '../isQueryInfo';

export const migrateEnterpriseToAtlassian: TrelloRestResolver<MutationMigrateEnterpriseToAtlassianArgs> = async (
  _parent,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${args.idEnterprise}/migrate`,
  );

  const response = await fetch(apiUrl, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });

    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const updateEnterpriseMultipleEmailMembersPrimaryEmail: TrelloRestResolver<MutationUpdateEnterpriseMultipleEmailMembersPrimaryEmailArgs> = async (
  _parent,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${args.idEnterprise}/multiEmailMembers/primary`,
  );

  const response = await fetch(apiUrl, {
    method: 'PUT',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-Trello-Client-Version': context.clientAwareness.version,
    },
    body: JSON.stringify({
      token: context.token || token,
      members: args.members,
    }),
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });

    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};

export const enterpriseMultipleEmailMembersResolver: TrelloRestResolver<QueryEnterpriseMultipleEmailMembersArgs> = async (
  _parent,
  args,
  context,
  info,
) => {
  const rootNode = isQueryInfo(info) ? info.field : info.fieldNodes[0];
  const networkClient = getNetworkClient();
  const apiUrl = networkClient.getUrl(
    `/1/enterprises/${args.idEnterprise}/multiEmailMembers?count=${args.count}&offset=${args.offset}`,
  );

  const response = await trelloFetch(apiUrl, undefined, {
    clientVersion: context.clientAwareness.version,
    networkRequestEventAttributes: {
      source: 'graphql',
      resolver: 'EnterpriseMultipleEmailMembers',
      operationName: context.operationName,
    },
  });

  if (!response.ok) {
    sendNetworkErrorEvent({
      url: apiUrl,
      response: await response.clone().text(),
      status: response.status,
      operationName: context.operationName,
    });

    throw await parseNetworkError(response);
  }

  const body = await response.json();

  return prepareDataForApolloCache(body, rootNode);
};
